import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  makeStepIndexOf,
  savedToOrder,
  sequenceReviewFields,
  TFieldSequenceAdminView,
  TSavedItem,
} from "../components/FieldSequence/fieldSequenceOrder";
import { branchFeatureFlags } from "../config/branchFeatureFlags";
import { _axios } from "../utils/axios";

const BASE = "/field-sequence";
const KEY = "field-sequence";

/**
 * The order the applicant's screens follow (the applicant form, their View
 * Application, their payment details screen and the resubmit preview;
 * fields and empty spaces, first position first), or null while they should
 * keep their default order (switch off, nothing saved, not loaded yet, or a
 * municipality without the feature).
 */
export function useApplicantFormFieldSequence() {
  return useQuery({
    queryKey: [KEY, "form"],
    queryFn: () =>
      _axios.get<{ data: { enabled: boolean; items: TSavedItem[] } }>(BASE),
    select: (d) => {
      const data = d?.data?.data;
      return data?.enabled && data.items?.length ? savedToOrder(data.items) : null;
    },
    enabled: branchFeatureFlags.ENABLE_FIELD_SEQUENCE,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * The order the reviewers' screens follow (L1/L2/L3 application details,
 * the Admin/reviewer View Application and their PDF), or null while they
 * keep their default order. It reads the same reply as the applicant
 * form's hook, so the two share one request; only the switch differs.
 */
export function useReviewFieldSequence() {
  return useQuery({
    queryKey: [KEY, "form"],
    queryFn: () =>
      _axios.get<{
        data: { enabled: boolean; reviewEnabled?: boolean; items: TSavedItem[] };
      }>(BASE),
    select: (d) => {
      const data = d?.data?.data;
      return data?.reviewEnabled && data.items?.length ? savedToOrder(data.items) : null;
    },
    enabled: branchFeatureFlags.ENABLE_FIELD_SEQUENCE,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * A reviewer's list of fields in the same order as the application details
 * above it, for the "Select Change Fields" pickers on the L1/L2/L3 review
 * screens. The pickers listed the fields in the form's default order, so
 * after the Admin changed the sequence the picker no longer matched the
 * details list. It follows the reviewers' switch, like the details lists;
 * with the switch off or nothing saved the list comes back unchanged.
 */
export function useReviewOrderedFields<F extends { id: number; uid: string }>(
  fields: readonly F[],
  steps: Parameters<typeof makeStepIndexOf>[0] | undefined
): F[] {
  const { data: reviewFieldOrder } = useReviewFieldSequence();
  return useMemo(
    () => sequenceReviewFields<F>(fields, reviewFieldOrder, makeStepIndexOf(steps || [])),
    [fields, reviewFieldOrder, steps]
  );
}

/** The switches, the applicant form's fields and the saved order, for the Admin screen. */
export function useFieldSequenceAdminView() {
  return useQuery({
    queryKey: [KEY, "admin"],
    queryFn: () => _axios.get<{ data: TFieldSequenceAdminView }>(`${BASE}/admin`),
    select: (d) => d?.data?.data,
    enabled: branchFeatureFlags.ENABLE_FIELD_SEQUENCE,
    staleTime: 0,
  });
}

/**
 * Switches between the Admin's order and the default one, for the applicant
 * form ({ enabled }) or the review screens ({ reviewEnabled }). Everything is
 * read again afterwards.
 *
 * BEFORE - only the applicant form's switch:
 *   mutationFn: (body: { enabled: boolean }) => ...
 */
export function useSetFieldSequenceSetting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { enabled: boolean } | { reviewEnabled: boolean }) =>
      _axios.put(`${BASE}/setting`, body),
    retry: false,
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

/** Saves the Admin's order, first position first. Everything is read again afterwards. */
export function useSaveFieldSequence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { items: TSavedItem[] }) => _axios.put(BASE, body),
    retry: false,
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}
